import { motion } from "framer-motion";
import { Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { LogoIcon } from "@/components/Logo";

// Social Icons as inline SVGs for consistency
const TwitterXIcon = ({ size = 18, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const InstagramIcon = ({ size = 18, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const FacebookIcon = ({ size = 18, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { 
      name: "Instagram", 
      href: "https://www.instagram.com/wherewasiapp?igsh=dG5mYjNxbGtpb2Uw&utm_source=qr", 
      icon: InstagramIcon,
      testId: "social-instagram"
    },
    { 
      name: "Facebook", 
      href: "https://www.facebook.com/profile.php?id=61564710616584&mibextid=wwXIfr&mibextid=wwXIfr", 
      icon: FacebookIcon,
      testId: "social-facebook"
    },
  ];

  return (
    <footer 
      data-testid="footer-section"
      className="py-16 md:py-20 px-6 md:px-12 bg-[#FDFBF7] border-t border-stone-100"
    >
      <div className="max-w-5xl mx-auto">
        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <p className="font-handwritten text-2xl md:text-3xl text-[#0CBCBF] mb-6">
            Ready to find your place again?
          </p>
          <Link
            to="/app"
            data-testid="footer-cta"
            className="inline-flex items-center gap-2 bg-[#F58227] text-white hover:bg-[#D96D19] rounded-full px-8 py-3 font-bold shadow-lg hover:shadow-[#F58227]/30 transition-all active:scale-95"
          >
            Start Free
          </Link>
        </motion.div>

        {/* Footer content */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 pt-8 border-t border-stone-100">
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
            <LogoIcon size={32} />
            <span className="font-semibold text-[#27272A]">Where was i</span>
          </Link>

          <nav className="flex items-center gap-6">
            <Link
              to="/about"
              data-testid="footer-link-about"
              className="text-[#52525B] hover:text-[#F58227] transition-colors text-sm"
            >
              About
            </Link>
            <Link
              to="/why-i-built-this"
              data-testid="footer-link-why"
              className="text-[#52525B] hover:text-[#F58227] transition-colors text-sm"
            >
              Why I Built This
            </Link>
            <Link
              to="/privacy"
              data-testid="footer-link-privacy"
              className="text-[#52525B] hover:text-[#F58227] transition-colors text-sm"
            >
              Privacy
            </Link>
            <Link
              to="/terms"
              data-testid="footer-link-terms"
              className="text-[#52525B] hover:text-[#F58227] transition-colors text-sm"
            >
              Terms
            </Link>
            <Link
              to="/contact"
              data-testid="footer-link-contact"
              className="text-[#52525B] hover:text-[#F58227] transition-colors text-sm"
            >
              Support
            </Link>
          </nav>

          {/* Social Links */}
          <div className="flex items-center gap-3">
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={social.testId}
                className="p-2 rounded-full text-[#A1A1AA] hover:text-[#F58227] hover:bg-[#F58227]/10 transition-all"
                aria-label={`Follow us on ${social.name}`}
              >
                <social.icon size={18} />
              </a>
            ))}
          </div>

          <div className="flex items-center gap-1.5 text-sm text-[#A1A1AA]">
            <span>Made with</span>
            <Heart size={12} className="text-[#F58227] fill-[#F58227]" />
            <span>© {currentYear}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
